import "core-js/modules/es6.array.map";
import "core-js/modules/es6.function.name";

/*
 * Copyright 2016 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import * as tslib_1 from "tslib";
import classNames from "classnames";
import * as React from "react";
import { polyfill } from "react-lifecycles-compat";
import { AbstractPureComponent2, Icon, Popover, Position, Utils as CoreUtils } from "@reactivepad/blueprint-core";
import * as Classes from "../common/classes";
import { columnInteractionBarContextTypes } from "../common/context";
import { LoadableContent } from "../common/loadableContent";
import { HeaderCell } from "./headerCell";
export function HorizontalCellDivider() {
  return React.createElement("div", {
    className: Classes.TABLE_HORIZONTAL_CELL_DIVIDER
  });
}

var ColumnHeaderCell =
/** @class */
function (_super) {
  tslib_1.__extends(ColumnHeaderCell, _super);

  function ColumnHeaderCell() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.state = {
      isActive: false,
      isEditing: false
    };

    _this.handleEdit = function (isEditing) {
      _this.setState({
        isEditing: isEditing
      });
    };

    _this.handlePopoverOpened = function () {
      return _this.setState({
        isActive: true
      });
    };

    _this.handlePopoverClosing = function () {
      return _this.setState({
        isActive: false
      });
    };

    return _this;
  }
  /**
   * This method determines if a `MouseEvent` was triggered on a target that
   * should be used as the header click/drag target. This enables users of
   * this component to render fully interactive components in their header
   * cells without worry of selection or resize operations from capturing
   * their mouse events.
   */


  ColumnHeaderCell.isHeaderMouseTarget = function (target) {
    return target.classList.contains(Classes.TABLE_HEADER) || target.classList.contains(Classes.TABLE_COLUMN_NAME) || target.classList.contains(Classes.TABLE_INTERACTION_BAR) || target.classList.contains(Classes.TABLE_HEADER_CONTENT);
  };

  ColumnHeaderCell.prototype.render = function () {
    var _a;

    var _b = this.props,
        // from IColumnHeaderCellProps
    enableColumnReordering = _b.enableColumnReordering,
        isColumnSelected = _b.isColumnSelected,
        menuIcon = _b.menuIcon,
        // from IColumnNameProps
    name = _b.name,
        nameRenderer = _b.nameRenderer,
        // from IHeaderProps
    spreadableProps = tslib_1.__rest(_b, ["enableColumnReordering", "isColumnSelected", "menuIcon", "name", "nameRenderer"]);

    var classes = classNames(spreadableProps.className, Classes.TABLE_COLUMN_HEADER_CELL, (_a = {}, _a[Classes.TABLE_HAS_INTERACTION_BAR] = this.context.enableColumnInteractionBar, _a[Classes.TABLE_HAS_REORDER_HANDLE] = this.props.reorderHandle != null, _a));
    return React.createElement(HeaderCell, tslib_1.__assign({
      isReorderable: this.props.enableColumnReordering,
      isSelected: this.props.isColumnSelected
    }, spreadableProps, {
      className: classes
    }), this.renderName(), this.maybeRenderContent(), this.props.loading ? undefined : this.props.resizeHandle);
  };

  ColumnHeaderCell.prototype.renderName = function () {
    var _this = this;

    var isEditing = this.state.isEditing;
    var _a = this.props,
        index = _a.index,
        loading = _a.loading,
        name = _a.name,
        nameRenderer = _a.nameRenderer,
        reorderHandle = _a.reorderHandle;
    var dropdownMenu = this.maybeRenderDropdownMenu();
    var defaultName = React.createElement("div", {
      className: Classes.TABLE_TRUNCATED_TEXT
    }, name);
    var nameComponent = React.createElement(LoadableContent, {
      loading: loading,
      variableLength: true
    }, nameRenderer == null ? defaultName : [nameRenderer(name, index)].map(function (_name) {
      return React.cloneElement(_name, {
        // @ts-ignore
        onEditChanged: function onEditChanged(isEditing) {
          _this.handleEdit(isEditing); // @ts-ignore


          _name.props.onEditChanged && _name.props.onEditChanged(isEditing);
        }
      });
    }).pop());
    var rootColumnClasses = classNames(Classes.TABLE_COLUMN_NAME, isEditing && Classes.TABLE_COLUMN_NAME_EDITING);

    if (this.context.enableColumnInteractionBar) {
      return React.createElement("div", {
        className: rootColumnClasses,
        title: name
      }, React.createElement("div", {
        className: Classes.TABLE_INTERACTION_BAR
      }, reorderHandle, dropdownMenu), React.createElement(HorizontalCellDivider, null), React.createElement("div", {
        className: Classes.TABLE_COLUMN_NAME_TEXT
      }, nameComponent));
    } else {
      return React.createElement("div", {
        className: rootColumnClasses,
        title: name
      }, reorderHandle, dropdownMenu, React.createElement("div", {
        className: Classes.TABLE_COLUMN_NAME_TEXT
      }, nameComponent));
    }
  };

  ColumnHeaderCell.prototype.maybeRenderContent = function () {
    if (this.props.children === null) {
      return undefined;
    }

    return React.createElement("div", {
      className: Classes.TABLE_HEADER_CONTENT
    }, this.props.children);
  };

  ColumnHeaderCell.prototype.maybeRenderDropdownMenu = function () {
    var _a;

    var _b = this.props,
        index = _b.index,
        menuIcon = _b.menuIcon,
        menuRenderer = _b.menuRenderer;

    if (!CoreUtils.isFunction(menuRenderer)) {
      return undefined;
    }

    var classes = classNames(Classes.TABLE_TH_MENU_CONTAINER, (_a = {}, _a[Classes.TABLE_TH_MENU_OPEN] = this.state.isActive, _a));
    return React.createElement("div", {
      className: classes
    }, React.createElement("div", {
      className: Classes.TABLE_TH_MENU_CONTAINER_BACKGROUND
    }), React.createElement(Popover, {
      content: menuRenderer(index),
      position: Position.BOTTOM,
      className: Classes.TABLE_TH_MENU,
      modifiers: {
        preventOverflow: {
          boundariesElement: "window"
        }
      },
      onOpened: this.handlePopoverOpened,
      onClosing: this.handlePopoverClosing
    }, React.createElement(Icon, {
      icon: menuIcon
    })));
  };

  ColumnHeaderCell.defaultProps = {
    isActive: false,
    menuIcon: "chevron-down"
  };
  ColumnHeaderCell.contextTypes = columnInteractionBarContextTypes;
  ColumnHeaderCell = tslib_1.__decorate([polyfill], ColumnHeaderCell);
  return ColumnHeaderCell;
}(AbstractPureComponent2);

export { ColumnHeaderCell };